import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { api } from '../../services/api';
import ConfirmModal from '../ConfirmModal';

/**
 * FIX-D327 (D-327 §2) - "חיבור ליומן גוגל" in the profile (OTE staff). One click -> Google -> back here
 * with ?gcal=connected|error|cancelled. Nothing is shown when the server has no Google Calendar client yet.
 */
export default function GoogleCalendarConnect() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);

  const statusQuery = useQuery({
    queryKey: ['gcal', 'status'],
    queryFn: async () => (await api.getGcalStatus()).data ?? null,
    staleTime: 30_000,
  });

  // The result of the Google round trip, once; then the address is cleaned.
  useEffect(() => {
    const r = searchParams.get('gcal');
    if (!r) return;
    const msg = searchParams.get('gcal_msg');
    if (r === 'connected') setNotice({ ok: true, text: t('gcal.connectedNotice') });
    else if (r === 'cancelled') setNotice({ ok: false, text: t('gcal.cancelledNotice') });
    else {
      const known = ['not_configured', 'state_expired', 'inactive_user', 'exchange_failed', 'wrong_account', 'no_refresh_token'];
      setNotice({ ok: false, text: msg && known.includes(msg) ? t(`gcal.errors.${msg}`) : t('gcal.errorNotice') });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('gcal');
    next.delete('gcal_msg');
    setSearchParams(next, { replace: true });
    void queryClient.invalidateQueries({ queryKey: ['gcal'] });
  }, [searchParams, setSearchParams, t, queryClient]);

  const connectMutation = useMutation({
    mutationFn: async () => (await api.getGcalConnectUrl()).data?.url,
    onSuccess: (url) => {
      if (url) window.location.assign(url);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => api.disconnectGcal(),
    onSuccess: () => {
      setNotice({ ok: true, text: t('gcal.disconnectedNotice') });
      void queryClient.invalidateQueries({ queryKey: ['gcal'] });
      void queryClient.invalidateQueries({ queryKey: ['workspace'] });
    },
  });

  const s = statusQuery.data;
  if (!s || !s.configured) return null;

  return (
    <div className="card">
      <section className="py-6 first:pt-0 last:pb-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex items-start gap-3">
            <CalendarDays size={20} className="text-surface-500 mt-0.5 shrink-0" aria-hidden />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-surface-900">{t('gcal.title')}</h2>
              <p className="text-sm text-surface-600 mt-0.5">
                {s.connected
                  ? t('gcal.connectedAs', { email: s.google_email ?? '' })
                  : s.needs_reconnect ? t('gcal.needsReconnect') : t('gcal.description')}
              </p>
            </div>
          </div>
          {s.connected ? (
            <button type="button" className="btn btn-secondary shrink-0" disabled={disconnectMutation.isPending} onClick={() => setConfirmOff(true)}>
              {t('gcal.disconnect')}
            </button>
          ) : (
            <button type="button" className="btn btn-primary shrink-0" disabled={connectMutation.isPending} onClick={() => connectMutation.mutate()}>
              {t('gcal.connect')}
            </button>
          )}
        </div>
        <div aria-live="polite">
          {notice && (
            <p role={notice.ok ? undefined : 'alert'} className={`mt-3 text-sm ${notice.ok ? 'text-success-700' : 'text-danger-700'}`}>{notice.text}</p>
          )}
          {(connectMutation.isError || disconnectMutation.isError) && (
            <p role="alert" className="mt-3 text-sm text-danger-700">
              {((connectMutation.error || disconnectMutation.error) as any)?.error?.message || t('gcal.errorNotice')}
            </p>
          )}
        </div>
      </section>
      {confirmOff && (
        <ConfirmModal
          title={t('gcal.disconnectTitle')}
          description={t('gcal.disconnectText')}
          confirmLabel={t('gcal.disconnect')}
          onCancel={() => setConfirmOff(false)}
          onConfirm={() => {
            setConfirmOff(false);
            disconnectMutation.mutate();
          }}
        />
      )}
    </div>
  );
}
